import Head from "next/head";
import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { WEB_URL } from "@/config/endpoints";
import IMAGES from "@/public/assets/img";
import FreightLoanData from "@/config/freightLoan_config";
import { useRouter } from "next/router";
import ContactFormStep1 from "@/component/shared/contactForm/contactFormStep1";
import ContactFormStep2 from "@/component/shared/contactForm/contactFormStep2";
import { FormProvider } from "@/component/shared/contactForm/formContext";
import HowWork from "@/component/shared/machinery-loan/HowWork";
import BuyerCredit from "@/component/shared/machinery-loan/buyer-credit";
import AccordianFaq from "@/component/shared/accordianfaq";
import BannerCTA from "@/component/shared/machinery-loan/banner-CTA";
import FreightExporterChallenge from "@/component/shared/machinery-loan/freightExporterChallenge";
import { generateFaqSchema } from "@/component/shared/faqSchema";

const FreightLoan = () => {
  const router = useRouter();
  const [mWidth, setMWidth] = useState(0);
  const [showNextStep, setShowNextStep] = useState(false);
  const topSectionRef = useRef(null);
  const faqSchema = generateFaqSchema(FreightLoanData.Faq);

  const handleWindowResize = () => {
    setMWidth(window.innerWidth);
  };

  useEffect(() => {
    handleWindowResize();
    window.addEventListener("load", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  const showSteps = () => {
    setShowNextStep(true);
  };
  
  const showFirstStep = () => {
    setShowNextStep(false);
  };

  return (
    <main className="pt-[110px]">
      <Head>
      <title>{FreightLoanData.seoData.title}</title>
          <meta name="description" content={FreightLoanData.seoData.description} />
          {/* OpenGraph tags */} 
          <meta property="og:title" content={FreightLoanData.seoData.title}/>
          <meta property="og:description" content={FreightLoanData.seoData.description} />
          <meta property="og:image" content={IMAGES.HOMEIMG.bigLogoNew.src} />
          <meta property="og:url" content={WEB_URL+router.asPath} />
          <link rel="canonical" href={WEB_URL+router.asPath} />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{__html: JSON.stringify(faqSchema)}}/>
      </Head>
      <section
        ref={topSectionRef}
        className={`relative bg-no-repeat sm:bg-center bg-cover bg-bottom min-h-[450px] sm:min-h-[270px]`}
      >
        {mWidth > 768 ? (
          <Image
            priority={true}
            src={IMAGES.FreightLoan.bgBannerDesktop}
            width={1366}
            height={550}
            className="absolute inset-0 max-w-none w-full h-full"
          />
        ) : (
          <Image
            priority={true}
            src={IMAGES.FreightLoan.bgBannerMobile}
            width={670}
            height={600}
            className="absolute inset-0 max-w-none w-full h-full"
          />
        )}
        <div className="max-w-[1200px] mx-auto relative px-3 xm:px-2 flex justify-center items-center sm:flex-col pb-2 sm:pt-5">
          <div className="w-2/5 sm:w-full">
            <h2 className="text-heading5 leading-[60px] sm:text-3xl font-medium text-[#072F5F] md:text-3xl">
              {FreightLoanData.bannerHeading}
            </h2>
            <h2 className="text-headingbig sm:text-3xl text-[#072F5F] font-medium mb-2 md:text-heading5">
              {FreightLoanData.subheading}
            </h2>
            <div className="bg-[#E3A003] text-center py-xsp px-2 rounded-xl mb-4 text-xl md:text-base2 sm:text-base text-white uppercase w-fit">
              {FreightLoanData.btntext}
            </div>
            <h1
              className="text-[#072F5F] sm:text-xxl mb-4 w-[90%] sm:w-full"
              dangerouslySetInnerHTML={{ __html: FreightLoanData.paraLoan }}
            />
            <div className="flex gap-6 sm:gap-1 justify-between">
              {FreightLoanData.loanInfo.map((item, index) => (
                <div
                  key={index}
                  className={`
                  ${index === 0 ? "sm:w-1/3" : "sm:w-1/4"}
                `}
                >
                  <Image
                    src={item.icon}
                    alt={item.description}
                    width={55}
                    height={50}
                    className="mb-1 h-[55px]"
                    priority={true}
                  />
                  <p className="text-[#1A1A1A] text-base font-medium sm:text-sm">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div className="w-2/5 sm:w-full pt-5 sm:pt-2 flex justify-center">
            <FormProvider>
              {!showNextStep ? (
                <ContactFormStep1
                  showStep1={showSteps}
                  pageTitle="freight-invoice-discounting-india"
                />
              ) : (
                <ContactFormStep2
                  showPrevStep={showFirstStep}
                  formType="GetInTouchNew"
                />
              )}
            </FormProvider>
          </div>
        </div>
      </section>
      <section className="mt-[100px] sm:my-20">
        <div className="container px-3 lg:px-3 xm:px-2">
          <h2 className="text-textxl font-medium text-center text-baseBlack">
            {FreightLoanData.Challenge.sectionTitle}
          </h2>

          <h3 className="text-heading5 font-medium text-baseBlack mt-1 text-center sm:text-2xl">
            {FreightLoanData.Challenge.mainHeading}
          </h3>

          <p
            className="text-lg mt-4 text-baseBlack text-center"
            dangerouslySetInnerHTML={{ __html: FreightLoanData.Challenge.description }}
          />
        </div>
      </section>
      <FreightExporterChallenge challenge={FreightLoanData.Challenge} />
      <HowWork howItWork={FreightLoanData.HowItWorks} />
      <BuyerCredit buyerCredit={FreightLoanData.buyerCredit} />

      <section className="bg-white sm:py-[70px] py-[100px] grid place-items-center">
        <div className="container px-3 xm:px-2 ">
          <div>
            <h3 className="mb-6 font-bold text-center text-lg text-secondary">
              {FreightLoanData.FaqHead}
            </h3>
          </div>
          <div className="w-full text-left bg-white">
            <div className="mx-auto max-w-[1100px] sm:w-auto">
              <AccordianFaq faqData={FreightLoanData.Faq} />
            </div>
          </div>
        </div>
      </section>
      <BannerCTA data={FreightLoanData.bannerCTA} targetRef={topSectionRef} />
    </main>
  );
};

export default FreightLoan;
